import { r as createLogger } from "../logger-BU8YqMGU.js";
import { requestOrientationPermission as requestOrientationPermission$1 } from "./permission-checker.js";
//#region ../src/sensors/gps.ts
/**
* GPS Module
*
* Handles Geolocation API access and device orientation/compass.
*/
const log = createLogger("GPS");
let watchId = null;
let orientationHandler = null;
/**
* Start watching GPS position.
* Idempotent: clears any existing watch before starting a new one
* (Issue 4, 2026-02-27 user feedback — prevents watch leaks when
* transitioning from warm-up to recording watch).
*/
function startGpsWatch(onPosition, onError) {
	if (!navigator.geolocation) {
		log.error("Geolocation API not available");
		return;
	}
	stopGpsWatch();
	watchId = navigator.geolocation.watchPosition((pos) => {
		onPosition({
			lat: pos.coords.latitude,
			lon: pos.coords.longitude,
			altitude: pos.coords.altitude,
			accuracy: pos.coords.accuracy,
			altitudeAccuracy: pos.coords.altitudeAccuracy,
			heading: pos.coords.heading,
			speed: pos.coords.speed,
			timestamp: pos.timestamp
		});
	}, (err) => {
		log.error("Error:", err.message);
		onError?.(err);
	}, {
		enableHighAccuracy: true,
		maximumAge: 5e3,
		timeout: 15e3
	});
	log.info("Watch started");
}
/**
* Stop watching GPS position
*/
function stopGpsWatch() {
	if (watchId !== null) {
		navigator.geolocation.clearWatch(watchId);
		watchId = null;
		log.info("Watch stopped");
	}
}
/**
* Start listening for device orientation (compass)
*/
function startOrientationWatch(onOrientation) {
	stopOrientationWatch();
	orientationHandler = (event) => {
		onOrientation({
			alpha: event.alpha,
			beta: event.beta,
			gamma: event.gamma,
			absolute: event.absolute
		});
	};
	window.addEventListener("deviceorientation", orientationHandler);
	log.info("Orientation watch started");
}
/**
* Stop listening for device orientation
*/
function stopOrientationWatch() {
	if (orientationHandler) {
		window.removeEventListener("deviceorientation", orientationHandler);
		orientationHandler = null;
		log.info("Orientation watch stopped");
	}
}
/**
* Request permission for device orientation (required on iOS 13+).
*
* Boolean-contract wrapper around the permission-checker's
* `requestOrientationPermission` superset (quality-review A-4 — the two
* public implementations had already drifted once; this one lacked the
* missing-API guard and threw a ReferenceError where `DeviceOrientationEvent`
* does not exist). `true` only for an explicit grant (or the non-iOS
* no-permission-needed case); denied, failed, or unsupported → `false`.
*/
async function requestOrientationPermission() {
	return (await requestOrientationPermission$1()).granted === true;
}
//#endregion
export { requestOrientationPermission, startGpsWatch, startOrientationWatch, stopGpsWatch, stopOrientationWatch };
